/**
 * Utils to process dynamic UI component input data before to load these components.
 * The main purpose is to resolve variable against REST resources, sub-resources or primitive variables.
 *
 * SIMPLE EXAMPLE
 * --------------
 *
 * When loading a dynamic UI component, for instance something equivalent to this:
 *
 *     <CaseDocumentCompliancePage
 *         checkList={[ ... ]}
 *         documents={[ key: 'IDE', title: "Current doc (IDE)", ... ]}
 *         nextPageLink="/api/cases/203982039/next"
 *     />
 *
 * items such as the `document.resource` will be retrieved from the REST API, such as `nextPageLink`.
 *
 * These items will be configured as "templated" in BPMN activities, so that the InputDataProcessor will be able to
 * resolve them.
 *
 * For this example, we might configure them like this (in BPMN activity input):
 *
 *     documents = [
 *       {
 *         key: 'IDE',
 *         title: {
 *           _resolver: 'template',
 *           _template: "Current doc ($key)"
 *         }
 *       },
 *       ...
 *     ]
 *     nextPageLink = {
 *       _resolver: 'resourceData',
 *       _jsonPath: "$._links.next.href"
 *     }
 *
 * Or event simpler (with implicit resolvers):
 *
 *     documents = [
 *       {
 *         key: 'IDE',
 *         title: { _template: "Current doc ($key)" }
 *       },
 *       ...
 *     ]
 *     nextPageLink = { _jsonPath: "$._links.next.href" }
 *
 * This example uses both of the 2 resolvers implemented, which are:
 *  - the template resolver: resolution of variables in a string
 *  - the resource data resolver: resolution of a JSON path against an object or REST resource
 *
 * VARIABLES IN TEMPLATE RESOLVER
 * ------------------------------
 *
 * Variables in templates can be local or any variable present in parent objects:
 *
 *     name = "My File"
 *     title = { _template: "Title for file '$name'" }
 *     step: {
 *       subTitle = { _template: "Sub-title for file '$name'" }
 *     }
 *
 * It's also possible to wrap variable names when they contain '.' or '-' characters.
 * All the following examples work:
 *
 *     myVar1 = { _template: "$next" }
 *     myVar2 = { _template: "${next}" }
 *     myVar3 = { _template: "${step.next-page}" }
 *
 * Note: for some simple variable, the JSON attribute order will NOT matter (variables like the 'name' right above),
 * but complex variables which have to be resolved first will need to be declared BEFORE the template usage. For instance:
 *
 *     index: { _jsonPath: "$.index" },
 *     label: { _template: "t('caseGlobalView.widgets.contracts.bundle.title', { index: $index, num: $index + 1 })" }
 *
 * TARGET RESOURCES IN RESOURCE DATA RESOLVER
 * ------------------------------------------
 *
 * When using the following simple form:
 *
 *     { _jsonPath: "$.score" }
 *
 * the resource data resolver will resolve the JSON path against current page resource, which depends on the page or
 * context in which the UI component is loaded (usually the REST API Case resource).
 *
 * However, resource data resolver can take an additional `_resource` parameter to target a previously resolved resource.
 *
 * For instance, we would be looking for multiple info in a document resource (score, fileId, ...), so we could
 * do something like this:
 *
 *     documents = [
 *       {
 *         key: 'IDE',
 *         docResource: {
 *           _jsonPath: { _template: "$._embedded.documents[?(@.documentKey=='$key')]" }
 *         },
 *         score: {
 *           _resource: 'docResource',
 *           _jsonPath: "$.score"
 *         },
 *         fileId: {
 *           _resource: 'docResource',
 *           _jsonPath: "$.fileId"
 *         }
 *       },
 *       ...
 *     ]
 *
 * so that `score` and `fileId` would be found in `docResource` variable.
 *
 * Of course, it would also work if `score` and `fileId` were to be wrapped in one or more child objects of the
 * document item.
 *
 * COMPOSED RESOLVERS
 * ------------------
 *
 * Usually, it really useful to compose resolvers (in order to resolve sub-resources in an array, depending on a local
 * variable). For instance, access to document resources can be configured this way:
 *
 *     documents = [
 *       {
 *         key: 'IDE',
 *         resource: {
 *           _jsonPath: { _template: "$._embedded.documents[?(@.documentKey=='$key')]" }
 *         }
 *       },
 *       ...
 *     ]
 *
 * which is resolved to:
 *
 *     documents = [
 *       {
 *         key: 'IDE',
 *         resource: {
 *           fileName: 'my-file.png',
 *           mimeType: 'image/png',
 *           ...
 *         }
 *       },
 *       ...
 *     ]
 *
 * So here, the JSON path is templated using `$key` variable from the documents array item itself, and then it is
 * resolved against the current page resource.
 *
 * This work for '_template' attribute of the template resolver, and also for the '_jsonPath' attribute of the
 * resource data resolver.
 *
 * INDEX VARIABLES
 * ---------------
 *
 * Specific variables are created to reference current index in an array, for instance:
 *
 *     documents = [
 *       { index: { _template: "$INDEX" } },
 *       { index: { _template: "$INDEX" } }
 *     ]
 *
 * would be resolved like this:
 *
 *     documents = [
 *       { index: 0 },
 *       { index: 1 }
 *     ]
 *
 * As you can see, it's reolved as integers automatically (which is a feature of the template resolver): every
 * template containing only a single number variable will be converted in number instead of string.
 *
 * Another feature is that you can use name indexes, which is useful when using multi-dimensionnal arrays. This:
 *
 *     documents = [
 *       {
 *         index: { _template: "$INDEX" },
 *         items: [{
 *           globalId: { _template: "$documents_INDEX" },
 *           id: { _template: "$INDEX" },
 *           idAlias: { _template: "$items_INDEX" }
 *         },{
 *           globalId: { _template: "$documents_INDEX" },
 *           id: { _template: "$INDEX" },
 *           idAlias: { _template: "$items_INDEX" }
 *         }]
 *       },
 *       { index: { _template: "$INDEX" } }
 *     ]
 *
 * would be resolved like this:
 *
 *     documents = [
 *       {
 *         index: 0,
 *         items: [{
 *           globalId: 0,
 *           id: 0,
 *           idAlias: 0
 *         },{
 *           globalId: 0,
 *           id: 1,
 *           idAlias: 1
 *         }]
 *       },
 *       { index: 1 }
 *     ]
 *
 * where all $INDEX variables are resolved locally (<=> index in current array), and $<arrayName>_INDEX are
 * accessible in every child objects.
 *
 * PARENT OBJECT AUTO DUPLICATION
 * ------------------------------
 *
 * In some cases, a JSON resource selector might return several results (an array of resources) that we
 * want to process separately. A feature allows us to automatically duplicate parent object for a given
 * resource selector.
 *
 * Consider this case:
 *
 *  {
 *    docs: [{
 *      key: 'ADDITIONAL',
 *      docResource: {
 *        _jsonPath: { _template: "\$._embedded.documents[?(@.documentKey=='\$key')]" }
 *      },
 *      fileId: { _resource: 'docResource', _jsonPath: "\$.fileUuid" },
 *    }]
 *  }
 *
 * When selecting 'docResource' matching a path for 'ADDITIONAL' documents, if we get 1 resource, we want it to be used
 * as reference for the 'fileId' resolution (since it's a '_jsonPath' resolved on that resource).
 *
 * But it could also return several values! In this case, we want to duplicate the 'doc' item (in 'docs' array), with
 * each new element having a single 'docResource'. To achieve that, just add a '_duplicateParentIfArray' attribute
 * like this:
 *
 *  {
 *    docs: [{
 *      key: 'ADDITIONAL',
 *      docResource: {
 *        _jsonPath: { _template: "\$._embedded.documents[?(@.documentKey=='\$key')]" },
 *        _duplicateParentIfArray: true
 *      },
 *      fileId: { _resource: 'docResource', _jsonPath: "\$.fileUuid" },
 *    }]
 *  }
 *
 * RESOLVE ATTRIBUTE AS ARRAY
 * --------------------------
 * The _resolveAsArray boolean flag forces the attribute to be resolved as an array when set to true.
 *
 * This is needed because we are using json queries for the resolution of jsonPath and
 * json queries always return an array even when the attribute we are referencing is an object or a primitive type.
 * To mitigate this the default mechanism is to unpack one element arrays.
 * However sometimes we may know in advance that an attribute is an array,
 * by default the mechanism will still force the unpacking if the array contains only one element
 * The _resolveAsArray flag allows for this attribute to always be resolved as an array
 * even if it contains only one element, thus an item configured as follows
 * documents = [
 *       {
 *         key: 'IDE',
 *         resource: {
 *           _jsonPath: $.values",
 *           _resolveAsArray: true
 *         }
 *       },
 *       ...
 *     ]
 * will always be resolved as
 * documents = [
 *       {
 *         key: 'IDE',
 *         resource: [ "firstElement", ... ]
 *       },
 *       ...
 *     ]
 */

// import JSONPath from 'JSONPath' >> avoided for performance issues in large JSON document to resolve (more than
// 10s for 150 SOA3 document types, including lvl 1, 2 and 3, while @f5io/jsonpath is doing less than 100ms)

// Might not always return valid result, but it's really fast >> incompatible module build
// import jp from '@f5io/jsonpath'
// Seems pretty fast (not tested though). Some benchmark: https://github.com/andykais/json-querying-performance-testing
import { JSONPath } from 'jsonpath-plus' // Seems pretty fast (not tested though). Some benchmark: https://github.com/andykais/json-querying-performance-testing

import deepEquals from 'fast-deep-equal'

import { resolveKeyPathString } from '../../../shared/helpers/i18nHelpers'

const DEBUG = false
let log = function () {}

if (DEBUG) {
  log = function () {
    console.log.apply(console, arguments)
  }
}

/*
 * This translation pattern corresponds to what we will receive as input data from the server in some strings.
 * For instance, we will have these options:
 *
 * {
 *   "title": "t('myModule.someSection.title')"
 * }
 *
 * and we will need to detect such string values and replace them with their translation.
 */
const TRANSLATION_EXPR_START = 't('

/**
 * This string corresponds to the variable marker symbol in translation keys, for instance: "t('myKey.$code.label')"
 */
const TRANSLATION_VARIABLE_MARKER = '$'

/**
 * This variable is here to create a consistency between the code which returns this value, and the code which
 * processes array items. It indicates that an array item was remove, hence its value should be ignored.
 */
const VALUE_OF_ARRAY_ITEM_TO_REMOVE = undefined

/**
 * This variable is here to distinguish between "no variable dependency" for an attribute, and "unknown variable dependencies".
 * Usually, complex objects / array will fall into the later category.
 */
const UNKNOWN_DEPENDENCIES = 'UNKNOWN_DEPENDENCIES'

/**
 * An attribute name to mark the resolved resources.
 *
 * The reason we need this, is because when an array of resolved resources in injected with parent object duplication,
 * the first item is considered as an unresolved resource, but the next items in the parent array (which have just been injected)
 * would be considered as objects.
 *
 * By marking the resources, we can ignore them later on.
 */
const RESOLVED_RESOURCE_MARKER = '__internal_resolvedResource'

// ====== Deep Composed Resolver ============================================ //

const deepResolveObject = (sourceObj, variables, resolvers, i18n) => {
  const sourceObjStr = JSON.stringify(sourceObj)
  const resolved = deepResolveObjectWithParent(null, null, JSON.parse(sourceObjStr), variables, resolvers, i18n)

  if (deepEquals(resolved, sourceObj)) {
    return sourceObj
  }
  return resolved
}

const deepResolveObjectWithParent = (parentObj, attrNameInParent, sourceObj, variables, resolvers, i18n) => {
  log(`Deep resolve inside parent ${attrNameInParent}`, parentObj)
  log('Deep resolve source obj', sourceObj)
  log('Deep resolve variables', variables)

  // To resolve an array of string
  // ["t('i18n.widgets.documents.tabs.applicant1')", "t('i18n.widgets.documents.tabs.applicant2')"]
  if (typeof sourceObj === 'string') {
    return resolveKeyPathString(sourceObj, i18n, mergeVariables(variables))
  }

  if (sourceObj && sourceObj[RESOLVED_RESOURCE_MARKER]) {
    return sourceObj
  }

  let obj = sourceObj

  if (typeof sourceObj === 'object' && sourceObj != null && !Array.isArray(sourceObj)) {
    obj = { ...sourceObj }
  }

  if (isDirectResolvableObject(obj)) {
    const result = resolve(obj, variables, resolvers, true) // final call => we resolve it even if some variables are missing
    const resolved = result.output
    log(
      `Directly resolved ${attrNameInParent}`,
      resolved,
      DEBUG && `missing deps: ${setToString(result.missingDependencies)}`,
    )
    return resolved
  }

  // First phase for simple variables resolution

  let currentVariables = {}
  const primitiveVariables = {}
  const simplyResolvedVariables = {}

  // First phase for primitive type registration

  for (var propName in obj) {
    const prop = obj[propName]

    if (propName == '_resolver') {
      continue
    }

    if (typeof prop === 'string') {
      if (isTranslationString(prop) && prop.indexOf(TRANSLATION_VARIABLE_MARKER) < 0) {
        // basic translation
        primitiveVariables[propName] = resolveKeyPathString(prop, i18n)
      } else {
        primitiveVariables[propName] = prop
      }
    } else if (typeof prop === 'number' || !prop) {
      primitiveVariables[propName] = prop
    }
  }

  currentVariables = { ...primitiveVariables }

  // Then, we resolve variables which can easily be resolved (without dependencies)

  const dependenciesMap = new Map()

  for (const propName in obj) {
    const prop = obj[propName]

    log(`Resolving variable ${propName}`, prop)

    // Note:
    //
    // Dependencies of complex raw object (array of objects, ...), are not recursively extracted,
    // so we choose (for simplicity matters) to ignore these and categorize them with UNKNOWN_DEPENDENCIES value.
    // These attributes will be processed last.

    if (isDirectResolvableObject(prop)) {
      const result = resolve(prop, [...variables, currentVariables], resolvers, false)
      const resolved = result.output
      log(`Resolved variable ${propName}`, resolved, DEBUG && setToString(result.dependencies))

      if (resolved !== prop) {
        simplyResolvedVariables[propName] = resolved
      }
      dependenciesMap.set(propName, result.dependencies)
    } else {
      if (!(propName in primitiveVariables)) {
        simplyResolvedVariables[propName] = prop
      }
      // Objects or arrays of complex items should be resolved last, they using implies loops over several items
      const deps = isObjectContainingDirectlyResolvableObjects(prop) ? UNKNOWN_DEPENDENCIES : new Set()
      dependenciesMap.set(propName, deps)
    }
  }
  log('Dependencies map', DEBUG && depsMapToString(dependenciesMap))

  // Building a list of attributes for non-array objects, this will allow us to loop on objects, or arrays,
  // with the capability to insert multiple elements instead of one when obj is an array (+ its going to be ordered)

  const isObjAnArray = Array.isArray(obj)
  let objPropNames = []
  let nbOfIteration = 0

  if (isObjAnArray) {
    nbOfIteration = obj.length
  } else {
    const orderedAttributes = extractOrderedAttributesFromDepsMap(dependenciesMap, [...variables, currentVariables])
    log('Ordered attributes', DEBUG && setToString(orderedAttributes))

    objPropNames = [...orderedAttributes] // set -> array
    nbOfIteration = objPropNames.length
  }

  // Updating variables AFTER the attribute order extraction

  currentVariables = { ...currentVariables, ...simplyResolvedVariables }
  log('Current variables', currentVariables)

  // Finally we use previously registered resolved variables to resolve all complex variables
  // => loop on the object attributes / on the array elements

  log(`Number of props / items to iterate through in current object: ${nbOfIteration}`)

  for (let iAttr = 0; iAttr < nbOfIteration; ++iAttr) {
    const attrName = isObjAnArray ? iAttr : objPropNames[iAttr]
    const attrVal = obj[attrName]

    if (typeof attrVal === 'object') {
      if (Array.isArray(attrVal)) {
        const initialLength = attrVal.length
        for (let i = 0; i < attrVal.length; ++i) {
          log(`Resolving array attribute ${attrName} of parent object, item ${i}`, attrVal[i])

          const duplicateParentIfArray = !!attrVal[i] && attrVal[i]['_duplicateParentIfArray'] === true

          const newItem = deepResolveObjectWithParent(
            attrVal,
            i,
            attrVal[i],
            [
              ...variables,
              currentVariables,
              {
                INDEX: i,
                [`${attrName}_INDEX`]: i,
              },
            ],
            resolvers,
            i18n,
          )
          log(`Resolved item ${i} of array ${attrName}`, newItem)

          if (newItem === VALUE_OF_ARRAY_ITEM_TO_REMOVE && attrVal.length < initialLength) {
            // During the array processing, the current element has been resolved to undefined,
            // and it has been removed from the array => the loop must continue from the current
            // index which now represents the next element
            --i
            continue
          }

          if (duplicateParentIfArray && Array.isArray(newItem)) {
            // Here, we consider this use case:
            //  docResources: [
            //    {
            //      _jsonPath: { _template: "\$._embedded.documents[?(@.documentKey=='ADDITIONAL')]" },
            //      _duplicateParentIfArray: true,
            //    },
            //    {
            //      _jsonPath: { _template: "\$._embedded.documents[?(@.documentKey=='ADDITIONAL2')]" },
            //      _duplicateParentIfArray: true,
            //    }
            //  ]
            //
            // Variables would represent this:
            //   > attrVal: docResources
            //   > i: 0
            //
            // If 1 or more item(s) of an array are resolved as arrays + they have their '_duplicateParentIfArray' === true
            // Then we would expect to duplicate these whole items as much as there is of items
            // in the array resolved from 'docResources' items themselves
            //
            // Consequently, the resulting docResources array would be a concatenation of all ADDITIONAL,
            // and all ADDITIONAL2 documents.

            // at index i, it will remove 1 element (previous item), and insert new element
            attrVal.splice.apply(attrVal, [i, 1, ...newItem])

            // update index i to match last new element index
            i = i - 1 + newItem.length

            log('Array copy after array insert', DEBUG && JSON.stringify(attrVal))
          } else {
            attrVal[i] = newItem
          }
        }
        obj[attrName] = cleanUpArray(attrVal)
      } else {
        log(`Resolving object attribute ${attrName} of parent object`)

        const duplicateParentIfArray = !!obj[attrName] && obj[attrName]['_duplicateParentIfArray'] === true

        let resolved = deepResolveObjectWithParent(obj, attrName, attrVal, [...variables, currentVariables], resolvers, i18n)

        if (isObjAnArray) {
          // This might have changed during the child resolution (items duplication).
          // It is the case when an array contains an array which contains items with _duplicateParentIfArray.
          nbOfIteration = obj.length
        }

        if (duplicateParentIfArray && Array.isArray(parentObj) && typeof attrNameInParent === 'number') {
          log(`Items to duplicate the parent of ${attrName}`, resolved)

          const indexInParent = attrNameInParent // just for clarity

          if (resolved !== undefined && resolved !== null) {
            // only can duplicate on distinguishable objects

            if (!Array.isArray(resolved)) {
              // single items would have been unwrapped, so we rewrap them (not just object, but even strings)
              resolved = [resolved]
            }

            // Here, we consider this use case:
            //  {
            //    docs: [{
            //      key: 'ADDITIONAL',
            //      docResource: {
            //        _jsonPath: { _template: "\$._embedded.documents[?(@.documentKey=='\$key')]" },
            //        _duplicateParentIfArray: true,
            //      },
            //      fileId: { _resource: 'docResource', _jsonPath: "\$.fileUuid" },
            //    }]
            //  }
            //
            // Variables would represent this:
            //   > parentObj: docs
            //   > attrNameInParent: 0
            //   > obj: the first item of docs
            //   > attrName: 'docResource'
            //
            // If docResource is an array + has its '_duplicateParentIfArray' === true
            // Then we would expect to duplicate the whole first docs item as much as there is of items
            // in the array resolved from 'docResource'
            //
            // Consequently, the resulting docs array would have several item for which key = 'ADDITIONAL',
            // and each of these item would have a docResource object (and not the entire docResource array).
            // It would have been spread through several duplicated docs items.

            // First we build copy of the elements

            const newParentElements = []

            for (const resolvedItem of resolved) {
              const parentItemCopy = JSON.parse(JSON.stringify(parentObj[indexInParent])) // deep copy
              parentItemCopy[attrName] = resolvedItem
              newParentElements.push(parentItemCopy)
            }

            // Then we insert these new items in the parent

            log('Parent obj before array insert in object', DEBUG && JSON.stringify(parentObj))
            log(`Will insert at index ${indexInParent}`, newParentElements)

            // at index indexInParent, it will remove 1 element (previous item), and insert new elements
            parentObj.splice.apply(parentObj, [indexInParent, 1, ...newParentElements])

            log('Parent obj after array insert in object', DEBUG && JSON.stringify(parentObj))

            if (resolved.length == 0) {
              // it means that the 'splice' call on parentObj removed an item
              // No need to keep processing the current element, no duplication element(s) were found in the data (from JSON path)
              // => we return undefined to indicate it should be removed
              return VALUE_OF_ARRAY_ITEM_TO_REMOVE
            }
            currentVariables[attrName] = resolved[0]
            obj[attrName] = resolved[0]
          } else {
            parentObj.splice.apply(parentObj, [indexInParent, 1]) // removing this item
            return VALUE_OF_ARRAY_ITEM_TO_REMOVE
          }
        } else {
          currentVariables[attrName] = resolved
          obj[attrName] = resolved
        }
      }
    } else if (typeof attrVal === 'string') {
      if (attrName in primitiveVariables && isTranslationString(attrVal)) {
        obj[attrName] = primitiveVariables[attrName] // copy translated key
      }
    }
  }

  // Private attributes cleanup

  removePrivateAttributes(obj)

  // Last phase: translation of strings

  let allVariables = null

  for (var propName in obj) {
    const prop = obj[propName]

    if (isTranslationString(prop)) {
      allVariables = memoize(allVariables, () => mergeVariables([...variables, currentVariables]))
      obj[propName] = resolveKeyPathString(prop, i18n, allVariables)
    }
  }

  return obj
}

/**
 * Function to compute an ordered list of attributes using a dependency map.
 *
 * For instance, you provide something like this: {
 *   label: Set { index },
 *   index: Set { bundleResource },
 *   bundleResource: Set {  },
 * }
 *
 * And it outputs something like this: Set { bundleResource, index, label }
 *
 * This is because the 'bundleResource' doesn't have any dependency, and the 'index' requires it,
 * and the same goes for the 'label' in respect to the 'index'.
 */
const extractOrderedAttributesFromDepsMap = (dependenciesMap, variables) => {
  const attributes = new Set()

  // Copy dependencies map + remove all provided dependencies

  const depsMap = new Map(dependenciesMap)
  for (const [attrName, initialDeps] of depsMap) {
    let deps = new Set()

    if (initialDeps == UNKNOWN_DEPENDENCIES) {
      deps = UNKNOWN_DEPENDENCIES
    } else if (initialDeps) {
      for (const depName of initialDeps) {
        const isSiblingDependency = depsMap.has(depName)
        // we may have problems with getFirstVariableMatch
        // especially when trying to find a variable of an object whose parent has already one of the same name
        if (
          isSiblingDependency ||
          (getFirstVariableMatch(variables, depName) === undefined && depName.indexOf('INDEX') < 0)
        ) {
          deps.add(depName)
        }
      }
    }
    depsMap.set(attrName, deps)
  }

  log('Initial cleaned copy of deps map', DEBUG && depsMapToString(depsMap))

  // Add all attributes without dependencies + remove them from other attributes deps + loop until finished

  let hasAddedSomething = true
  while (hasAddedSomething) {
    hasAddedSomething = false
    const attributesToAdd = []

    for (const [attrName, deps] of depsMap) {
      if (deps != UNKNOWN_DEPENDENCIES && (!deps || deps.size <= 0)) {
        attributesToAdd.push(attrName)
      }
    }

    if (attributesToAdd.length > 0) {
      hasAddedSomething = true

      for (const attrNameToAdd of attributesToAdd) {
        attributes.add(attrNameToAdd)
        depsMap.delete(attrNameToAdd)

        for (const [attrName, deps] of depsMap) {
          if (deps != UNKNOWN_DEPENDENCIES) {
            deps.delete(attrNameToAdd)
          }
        }
      }
    }
  }

  // Add all remaining attributes (not-resolved)

  for (const [attrName, deps] of depsMap) {
    attributes.add(attrName)

    if (deps == UNKNOWN_DEPENDENCIES) {
      log(`Found the attribute '${attrName}' whose dependencies are unknown: will be processed at the end`)
    } else {
      console.warn(
        `Found the attribute '${attrName}' whose dependencies couldn't be solved during input data processing: ${setToString(
          deps,
        )}. ${DEBUG ? `Current variables: ${JSON.stringify(variables)}` : ''}`,
      )
    }
  }
  return attributes
}

// ====== Resolution function =============================================== //

const resolve = (obj, variables, resolvers, evenWhenMissingDeps) => {
  let resolver = obj['_resolver']
  if (!resolver) {
    if (obj['_template']) {
      resolver = 'template'
    } else if (obj['_resource'] || obj['_jsonPath']) {
      resolver = 'resourceData'
    } else {
      console.error('Unable to find explicit resolver description (or to extract it implicitly) in object to resolve', obj)
      return obj
    }
  }
  return resolvers[resolver](obj, variables, evenWhenMissingDeps)
}

// ====== Resolvers ========================================================= //

const VARIABLE_REF_REGEXP = /\$(\{([\w._-]+)\}|([\w_]+))/g

/**
 * Resolver for this type of objects:
 * {
 *   _resolver: 'template',
 *   _template: "A template string with some variables like ${documents_INDEX}"
 * }
 *
 * It resolve a templated string, using given variables array (local variables are resolved prior to global ones).
 */
const TemplateResolver = (resolvers) => (obj, variables, evenWhenMissingDeps) => {
  let tpl = obj['_template']
  let subResult = null

  if (isDirectResolvableObject(tpl)) {
    subResult = resolve(tpl, variables, resolvers, evenWhenMissingDeps)
    tpl = subResult.output

    if (isDirectResolvableObject(tpl)) {
      // Template was not resolved
      if (evenWhenMissingDeps) {
        return {
          ...subResult,
          output: null,
        }
      }
      return {
        ...subResult,
        output: obj,
      }
    }
  }

  if (typeof tpl !== 'string') {
    // Resolved template might have been undefined, or anything else.
    // Actually we don't care: anything else than a string won't be processed for now. It'll just be returned as-is.
    return { output: tpl }
  }

  log('Resolving variables in template', tpl, variables)

  const result = resolveVariablesInTemplate(tpl, variables)
  const resolvedTpl = result.output

  log('Resolved template', resolvedTpl, DEBUG && setToString(result.missingDependencies))

  if (!evenWhenMissingDeps && typeof resolvedTpl === 'string' && result.missingDependencies.size > 0) {
    return extendResolutionResult(subResult, {
      ...result,
      output: obj, // Don't resolve object if there are some missing variables
    })
  }
  return extendResolutionResult(subResult, result)
}

const resolveVariablesInTemplate = (tpl, variables) => {
  let wasNumberFullMatch = false
  const dependencies = new Set()
  const missingDependencies = new Set()

  const resolvedTpl = tpl.replace(VARIABLE_REF_REGEXP, (match, g1, g2, g3, offset, fullString) => {
    const varName = typeof g2 !== 'string' ? g3 : g2
    const varValue = getFirstVariableMatch(variables, varName)

    dependencies.add(varName)

    if (typeof varValue === 'string') {
      return varValue
    }
    if (typeof varValue === 'number') {
      if (match == fullString) {
        wasNumberFullMatch = true
      }
      return varValue
    }
    // Non-resolved variable
    missingDependencies.add(varName)
    return match
  })

  return {
    output: postProcessResolvedTemplate(resolvedTpl, wasNumberFullMatch),
    dependencies: dependencies,
    missingDependencies: missingDependencies,
  }
}

const postProcessResolvedTemplate = (resolvedTpl, wasNumberFullMatch) => {
  if (wasNumberFullMatch) {
    return resolvedTpl.indexOf('.') > -1 ? parseFloat(resolvedTpl) : parseInt(resolvedTpl)
  }
  return resolvedTpl
}

/**
 * Resolver for this type of objects:
 * {
 *   _resolver: 'resourceData',
 *   _resource: 'documentResource', // (optional, default is current page main resource)
 *   _jsonPath: "$._embedded.documents[?(@.documentKey=='$documentKey')]"
 * }
 *
 * It resolve a JSON path against an external resource object or against an object variable:
 *
 *  > first, we use '_resource' attribute to resolve the resource of variable itself
 *  > then we resolve the '_jsonPath' like a templated string (see 'template' resolver)
 *  > finally, we use the JSON path to extract data from the resource object and return it
 */
const ResourceDataResolver = (resolvers, defaultResource) => (obj, variables, evenWhenMissingDeps) => {
  // Resolving resource

  const resName = obj['_resource']
  const dependencies = new Set()
  const missingDependencies = new Set()

  let jsonPath = obj['_jsonPath']
  let res = defaultResource
  let resWasNotFound = false
  let result = {
    dependencies,
    missingDependencies,
  }

  const resolveAsArray = obj['_resolveAsArray']

  if (typeof resName === 'string') {
    res = getFirstVariableMatch(variables, resName)
    dependencies.add(resName)

    if (res !== undefined && !jsonPath) {
      // This is the support for aliases (defined resource only, without JSON path).
      // In this case, we don't need much: no missing dependencies + forwarding the resource value
      if (resolveAsArray && !Array.isArray(res)) {
        res = [res]
      }

      return extendResolutionResult(result, { output: res })
    }

    if (typeof res !== 'object' || res == null) {
      missingDependencies.add(resName)
      resWasNotFound = true
    }
  }

  // Resolving JSON path as a template

  if (isDirectResolvableObject(jsonPath)) {
    result = extendResolutionResult(result, resolve(jsonPath, variables, resolvers, evenWhenMissingDeps))
    jsonPath = result.output
  }

  // Process unsolvable cases

  if (resWasNotFound || typeof jsonPath !== 'string') {
    if (evenWhenMissingDeps) {
      return extendResolutionResult(result, { output: null })
    }
    return extendResolutionResult(result, { output: obj })
  }

  // Resolving JSON path against resource

  const jpRes = JSONPath({
    path: jsonPath,
    json: res,
    wrap: false, // no need to rewrap it into an array, we don't want to change the nature of the result
  })
  return { output: postProcessJsonResolution(jpRes, resolveAsArray) }
}

const postProcessJsonResolution = (jpRes, resolveAsArray) => {
  if (jpRes === undefined) {
    return null // we want a "null" resolved variable value, so that we know it was processed
  }
  if (typeof jpRes === 'boolean') {
    return jpRes
  }
  if (typeof jpRes === 'object' && Array.isArray(jpRes)) {
    if (jpRes.length === 0) {
      // shouldn't be undefined, because it would mean that the variable doesn't exist
      // (for other objects depending on this)
      return resolveAsArray ? [] : null
    }
    for (const res of jpRes) {
      if (res && typeof res === 'object' && !Array.isArray(res)) {
        // we could also mark resources when they're not from arrays, but not really useful for now
        res[RESOLVED_RESOURCE_MARKER] = true
      }
    }
    // filters such as "$.items[?(@.id=='123')]" will return arrays, so we extract single items here
    if (jpRes.length == 1 && !resolveAsArray) {
      return jpRes[0]
    }
  }
  return jpRes
}

// ====== Utils ============================================================= //

const extendResolutionResult = (resultA, resultB) => {
  const mergedResult = {
    ...(resultA || {}),
    ...(resultB || {}),
  }
  mergedResult.dependencies = mergeSets(resultA?.dependencies, resultB?.dependencies)
  mergedResult.missingDependencies = mergeSets(resultA?.missingDependencies, resultB?.missingDependencies)
  return mergedResult
}

const mergeSets = (setA, setB) => {
  const merged = new Set()
  copySetItems(setA, merged)
  copySetItems(setB, merged)
  return merged
}

const copySetItems = (source, target) => {
  if (source) {
    for (const item of source) {
      target.add(item)
    }
  }
}

const getFirstVariableMatch = (variables, varName) => {
  for (let i = variables.length - 1; i >= 0; --i) {
    if (typeof variables[i][varName] !== 'undefined') {
      return variables[i][varName]
    }
  }
  return undefined
}

const depsMapToString = (depsMap) => {
  const entries = []
  for (const [key, value] of depsMap) {
    entries.push(`${key}: ${setToString(value)}`)
  }
  return `DepsMap { ${entries.join(', ')} }`
}

const setToString = (set) => {
  if (!set || typeof set !== 'object') {
    return `${set}`
  }
  const items = Array.from(set)
  return `Set { ${items.join(', ')} }`
}

const mergeVariables = (variables) => {
  const outputVariables = {}

  for (const vars of variables) {
    for (const varName in vars) {
      outputVariables[varName] = vars[varName]
    }
  }
  return outputVariables
}

const memoize = (value, computeFct) => value || computeFct()

const isTranslationString = (str) => typeof str === 'string' && str.indexOf(TRANSLATION_EXPR_START) == 0

const isObjectContainingDirectlyResolvableObjects = (obj) => {
  if (typeof obj !== 'object') {
    return false
  }
  if (Array.isArray(obj)) {
    return isArrayContainingDirectlyResolvableObjects(obj)
  }
  for (const attrName in obj) {
    if (isDirectResolvableObject(obj[attrName]) || isObjectContainingDirectlyResolvableObjects(obj[attrName])) {
      return true
    }
  }
  return false
}

const isArrayContainingDirectlyResolvableObjects = (obj) => {
  if (!Array.isArray(obj)) {
    return false
  }
  for (const item of obj) {
    if (isDirectResolvableObject(item) || isObjectContainingDirectlyResolvableObjects(item)) {
      return true
    }
  }
  return false
}

const isDirectResolvableObject = (obj) =>
  !!obj &&
  typeof obj === 'object' &&
  !Array.isArray(obj) &&
  (!!obj['_resolver'] || !!obj['_template'] || !!obj['_resource'] || !!obj['_jsonPath'])

const removePrivateAttributes = (obj) => {
  for (const propName in obj) {
    if (propName.startsWith('_')) {
      delete obj[propName]
    }
  }
}

const cleanUpArray = (obj) => {
  return obj.filter(function (item) {
    return item !== undefined && item !== null
  })
}

export default {
  TemplateResolver,
  ResourceDataResolver,
  deepResolveObject,

  // for tests only:
  extractOrderedAttributesFromDepsMap,
  isObjectContainingDirectlyResolvableObjects,
}
